import React from 'react'

/**
 * The workhorse container for admin pages. White card, rounded, subtle border
 * and shadow, with an optional title bar at the top.
 *
 * - `title`         small bold heading in the title bar
 * - `description`   optional tertiary subtitle
 * - `headerAction`  optional slot in the title bar (e.g. "+ Add" button)
 * - `padded`        set `false` if the content is a table or any element
 *                   that provides its own padding / dividers
 *
 * Usage:
 *   <SectionCard title="Appearance" description="Brand colour and avatar behaviour">
 *     ...fields...
 *   </SectionCard>
 */
export interface SectionCardProps {
  title?: string
  description?: string
  headerAction?: React.ReactNode
  padded?: boolean
  className?: string
  children: React.ReactNode
}

export function SectionCard({
  title,
  description,
  headerAction,
  padded = true,
  className = '',
  children,
}: SectionCardProps) {
  const hasHeader = Boolean(title || description || headerAction)
  return (
    <div className={`bg-surface rounded-xl border border-border overflow-hidden shadow-sm mb-6 ${className}`}>
      {hasHeader && (
        <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-4">
          <div>
            {title && <h3 className="text-sm font-semibold text-txt-primary">{title}</h3>}
            {description && <p className="text-xs text-txt-tertiary mt-1">{description}</p>}
          </div>
          {headerAction && <div className="shrink-0">{headerAction}</div>}
        </div>
      )}
      <div className={padded ? 'p-5' : ''}>{children}</div>
    </div>
  )
}
